import { Review } from "@core/domain/models/review.model";
import { IncidentMetadata } from "@core/domain/models/reviewItemMetadata/incidentMetadata";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import { ReviewItemType } from "@core/domain/types/reviewItemType.type";
import axios from "axios";

interface DateFilter {
  startDate?: string;
  endDate?: string;
}

export class OpenFdaApiClient {
  private readonly baseUrl = "https://api.fda.gov/device/event.json";

  async createReviewSearch(
    review: Review,
    query: string,
    dateFilter?: DateFilter,
  ): Promise<{
    query?: string;
    filters?: string;
    date: string;
    reviewId: number;
    importSourceId: string;
    items: IncidentMetadata & { type: ReviewItemType.Incident }[];
    queryTranslation?: string;
    url?: string;
  }> {
    const result = await this.search({ query, dateFilter });
    return {
      query,
      filters: dateFilter ? this.buildDateFilterString(dateFilter) : undefined,
      date: new Date().toISOString(),
      reviewId: review.id,
      importSourceId: BuiltInImportSourceId.OPEN_FDA,
      items: result.map((a) => ({
        ...a,
        type: ReviewItemType.Incident,
      })),
    };
  }

  async search({
    query,
    dateFilter,
  }: {
    query: string;
    dateFilter?: DateFilter;
  }): Promise<IncidentMetadata[]> {
    return this.searchRecords(query, dateFilter);
  }

  private async searchRecords(query: string, dateFilter?: DateFilter): Promise<IncidentMetadata[]> {
    let incidents: IncidentMetadata[] = [];
    let searchQuery = "";
    const queryParts = query
      .split("AND")
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    const deviceName = queryParts[0];
    const manufacturerName = queryParts[1];

    if (deviceName) {
      searchQuery += `device.brand_name:${deviceName}`;
    }
    if (manufacturerName) {
      if (searchQuery) {
        searchQuery += "+AND+";
      }
      searchQuery += `device.manufacturer_d_name:${manufacturerName}`;
    }

    if (dateFilter) {
      const dateFilterString = this.buildDateFilterString(dateFilter);
      if (dateFilterString) {
        searchQuery = searchQuery ? `${searchQuery}+AND+${dateFilterString}` : dateFilterString;
      }
    }

    const url = `${this.baseUrl}?search=${searchQuery}&limit=500`;
    try {
      const response = await axios.get(url);

      incidents = response.data.results.map((record: any) => this.mapToIncident(record));
    } catch (error) {
      console.error("Error fetching records from OpenFDA API:", error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return [];
      }
      throw error;
    }

    return incidents;
  }

  private mapToIncident(record: any): IncidentMetadata {
    return new IncidentMetadata({
      title: `${record.device[0].manufacturer_d_name} ${record.device[0].brand_name} ${record.device[0].generic_name}`,
      reportNumber: record.report_number,
      eventDate: record.date_of_event,
      eventType: record.event_type,
      reportDate: record.date_report,
      url: `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfMAUDE/detail.cfm?mdrfoi__id=${record.mdr_report_key}`,
      summary: record.mdr_text
        .map((mt: any) => `${mt.text_type_code}: ${this.capitalizeSentences(mt.text)}\n`)
        .join(""),
    });
  }

  private capitalizeSentences(text: string) {
    const lowercaseText = text.toLowerCase();

    const sentences = lowercaseText.split(". ");

    for (let i = 0; i < sentences.length; i++) {
      sentences[i] = sentences[i].charAt(0).toUpperCase() + sentences[i].slice(1);
    }

    const result = sentences.join(". ");

    return result;
  }

  private buildDateFilterString(dateFilter: DateFilter): string {
    const formatDate = (date: string): string => {
      const parsedDate = new Date(date);
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      return `${year}${month}${day}`;
    };

    if (dateFilter.startDate && dateFilter.endDate) {
      const startDateStr = formatDate(dateFilter.startDate);
      const endDateStr = formatDate(dateFilter.endDate);
      return `date_received:[${startDateStr}+TO+${endDateStr}]`;
    } else if (dateFilter.startDate) {
      const startDateStr = formatDate(dateFilter.startDate);
      return `date_received:[${startDateStr}+TO+*]`;
    } else if (dateFilter.endDate) {
      const endDateStr = formatDate(dateFilter.endDate);
      return `date_received:[*+TO+${endDateStr}]`;
    }

    return "";
  }
}
